// client/src/components/configuracoes/creditos/CreditosPacotesAdminPage.js
import React, { Fragment, useState, useCallback } from 'react';
import { Coins, Loader2, MoreVertical, Edit, Trash2 } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';
import ActionsDrawer from '../../common/ActionsDrawer';
import CreditosPacoteForm from './CreditosPacoteForm';
import { usePacotesCreditoAdmin, useMutatePacoteCredito } from '../../../hooks/useComunicacaoCredito';

// ✅ Administração dos pacotes vendáveis de Créditos de Comunicação — só
// Matriz gerencia (ver server/routes/comunicacaoCreditoRoutes.js,
// authorize('matriz') nas rotas /pacotes/admin). Conta só vê a lista
// pública de pacotes ativos, na seção de créditos em IntegracoesConfigPage.js.
const CreditosPacotesAdminPage = () => {
    const { data: pacotes, isLoading } = usePacotesCreditoAdmin();
    const { removerPacote } = useMutatePacoteCredito();

    const [showForm, setShowForm] = useState(false);
    const [current, setCurrent] = useState(null);
    const [selectedPacote, setSelectedPacote] = useState(null);

    const handleAddNew = useCallback(() => {
        setCurrent(null);
        setShowForm(true);
    }, []);

    const handleEditar = useCallback((pacote) => {
        setCurrent(pacote);
        setShowForm(true);
        setSelectedPacote(null);
    }, []);

    const handleRemover = useCallback((id) => {
        if (window.confirm('Deseja realmente remover este pacote de créditos?')) {
            removerPacote.mutate(id, { onSuccess: () => setSelectedPacote(null) });
        }
    }, [removerPacote]);

    const handleCloseForm = useCallback(() => {
        setShowForm(false);
        setCurrent(null);
    }, []);

    const formatarPreco = (centavos) => (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    const items = pacotes || [];

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Coins />}
                    title="Pacotes de Créditos de Comunicação"
                    subtitle={!isLoading ? `${items.length} pacote${items.length !== 1 ? 's' : ''}` : null}
                />
                {showForm && (
                    <div className="content-panel">
                        <CreditosPacoteForm current={current} onCloseForm={handleCloseForm} />
                    </div>
                )}
            </div>

            {!showForm && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoading ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin" />
                                <span>Carregando pacotes...</span>
                            </div>
                        ) : items.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left' }}>Nome</th>
                                        <th>Créditos</th>
                                        <th>Preço</th>
                                        <th>Status</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((pacote) => (
                                        <tr key={pacote._id} className="hover:bg-gray-50">
                                            <td className="py-3">{pacote.nome}</td>
                                            <td className="py-3">{pacote.quantidadeCreditos}</td>
                                            <td className="py-3">{formatarPreco(pacote.preco)}</td>
                                            <td className="py-3">
                                                <span className={`badge ${pacote.ativo ? 'badge-green' : 'badge-gray'}`}>
                                                    {pacote.ativo ? 'Ativo' : 'Inativo'}
                                                </span>
                                            </td>
                                            <td className="text-right">
                                                <button
                                                    onClick={() => setSelectedPacote(pacote)}
                                                    title="Ações"
                                                    className="action-button-icon text-gray-600 hover:text-gray-900"
                                                >
                                                    <MoreVertical size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <Coins size={48} />
                                <h3>Nenhum pacote de créditos cadastrado</h3>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <FabButton onClick={handleAddNew} visible={!showForm} />

            <ActionsDrawer
                open={!!selectedPacote}
                onClose={() => setSelectedPacote(null)}
                title={selectedPacote?.nome || 'Pacote'}
                actions={selectedPacote ? [
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <Edit size={20} className="icon" />,
                        onClick: () => handleEditar(selectedPacote),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleRemover(selectedPacote._id),
                        destructive: true,
                        loading: removerPacote.isPending,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default CreditosPacotesAdminPage;
